/*
 * Karigur — motion & interactions.
 *   • Hover-to-video               — [data-kb-hover-video] plays the look's video on hover
 *   • Magnetic CTA / soft tilt     — opt-in via [data-kb-magnetic] / [data-kb-tilt]
 *
 * Scrolling is native on every device. The Lenis smooth-scroll layer was removed on
 * 2026-10-06: it made a wheel gesture glide for about a second and stopped the wheel from
 * scrolling drawers and other inner panels. In-page anchor offsets are plain CSS now
 * (:target { scroll-margin-top } in the layouts).
 * Touch devices and prefers-reduced-motion users get static cards.
 * No custom cursor (removed — it cheapened the experience). :focus-visible is untouched.
 */
(function () {
  'use strict';

  var mqFine = window.matchMedia && window.matchMedia('(pointer: fine)').matches;
  var mqReduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var motionOK = !mqReduce;
  var each = function (l, f) { Array.prototype.forEach.call(l || [], f); };
  var lerp = function (a, b, n) { return a + (b - a) * n; };

  /* Card videos are multi-megabyte (the heaviest is ~11 MB). Never spend a metered or slow
     connection on decoration: Save-Data and any reported 2g/3g link get static cards. */
  function connectionIsExpensive() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    if (c.saveData) return true;
    var t = c.effectiveType || '';
    return t === 'slow-2g' || t === '2g' || t === '3g';
  }

  /* ---------------------------------------------------------------- *
   * Hover-to-video — the look comes alive on hover (desktop only).
   * Wrapper carries [data-kb-hover-video] and contains a <video>.
   * ---------------------------------------------------------------- */
  function initHoverVideo(el) {
    if (el.__kbHV) return; el.__kbHV = true;
    var v = el.querySelector('[data-kb-hover-video-el]') || el.querySelector('video');
    if (!v) return;
    // Start 1s in — the opening frame of bridal clips is usually a static hold;
    // a one-second seek lands on motion/fabric and reads far livelier. We seek
    // ONLY once metadata is known (seeking a preload=none video before it has
    // loaded stalls the fetch), and we reveal the video ONLY once it actually
    // has a paintable frame — so hover reads as a clean image → video dissolve
    // with no second-image flicker and no black flash (the poster covers the gap).
    var SEEK_TO = 1;
    function applySeek() {
      try {
        if (isFinite(v.duration) && v.duration > 0) {
          v.currentTime = v.duration <= 1.4 ? 0 : SEEK_TO;
        }
      } catch (e) {}
    }
    function markReady() { el.classList.add('is-video-ready'); }
    v.addEventListener('loadedmetadata', function () { if (el.classList.contains('is-playing')) applySeek(); });
    v.addEventListener('loadeddata', markReady);
    v.addEventListener('playing', markReady);
    el.addEventListener('pointerenter', function () {
      el.classList.add('is-playing');
      // Kick the fetch for a preload=none video, then play; seek lands via loadedmetadata.
      if (v.readyState === 0) { try { v.load(); } catch (e) {} }
      applySeek();
      var p = v.play();
      if (p && p.catch) p.catch(function () {});
    });
    el.addEventListener('pointerleave', function () {
      el.classList.remove('is-playing');
      el.classList.remove('is-video-ready');
      try { v.pause(); } catch (e) {}
    });
  }

  /* ---------------------------------------------------------------- *
   * Scroll-to-video — TOUCH / mobile. There is no hover on phones, so the
   * look comes alive as its card scrolls into view. Prominent luxury sites
   * (Sabyasachi, Dior, Saint Laurent) play exactly ONE video at a time — the
   * piece you are centred on — never a whole grid at once. So this is a single
   * global controller: as you scroll, it picks the card closest to the viewport
   * centre (that is at least half in view), plays that one, and pauses every
   * other. Same is-playing / is-video-ready classes → same slow dissolve as
   * desktop hover. Honours Save-Data / reduced-motion (gated in init()).
   * ---------------------------------------------------------------- */
  function initScrollVideoController(els) {
    var items = [];
    each(els, function (el) {
      if (el.__kbSV) return; el.__kbSV = true;
      var v = el.querySelector('[data-kb-hover-video-el]') || el.querySelector('video');
      if (!v) return;
      function applySeek() {
        try { if (isFinite(v.duration) && v.duration > 0) { v.currentTime = v.duration <= 1.4 ? 0 : 1; } } catch (e) {}
      }
      function markReady() { el.classList.add('is-video-ready'); }
      v.addEventListener('loadedmetadata', function () { if (el.classList.contains('is-playing')) applySeek(); });
      v.addEventListener('loadeddata', markReady);
      v.addEventListener('playing', markReady);
      items.push({ el: el, v: v, applySeek: applySeek });
    });
    if (!items.length) return;

    var active = null;
    function activate(item) {
      if (active === item) return;
      if (active) {
        active.el.classList.remove('is-playing');
        active.el.classList.remove('is-video-ready');
        try { active.v.pause(); } catch (e) {}
      }
      active = item;
      if (item) {
        item.el.classList.add('is-playing');
        if (item.v.readyState === 0) { try { item.v.load(); } catch (e) {} }
        item.applySeek();
        var p = item.v.play();
        if (p && p.catch) p.catch(function () {});
      }
    }

    var ticking = false;
    function pick() {
      ticking = false;
      var vh = window.innerHeight || document.documentElement.clientHeight;
      var center = vh / 2;
      var best = null, bestDist = Infinity;
      for (var i = 0; i < items.length; i++) {
        var r = items[i].el.getBoundingClientRect();
        if (r.height <= 0) continue;
        var visible = Math.min(r.bottom, vh) - Math.max(r.top, 0);
        if (visible <= 0) continue;
        // Eligible only when at least half the card (or half the viewport) is showing,
        // so a barely-peeking card at the screen edge never steals playback.
        if (visible / Math.min(r.height, vh) < 0.5) continue;
        var dist = Math.abs((r.top + r.height / 2) - center);
        if (dist < bestDist) { bestDist = dist; best = items[i]; }
      }
      activate(best); // best may be null (nothing eligible → pause all)
    }
    function onScroll() { if (!ticking) { ticking = true; requestAnimationFrame(pick); } }

    /* Arming is deliberately LATE. Calling pick() at init made the card nearest the viewport
       centre start streaming its video while the page was still loading — on a collection or
       landing page that is megabytes of video competing with the LCP image and the booking
       widget, on the connection of a bride on a phone. So: no playback until the page has
       finished loading AND the visitor has actually moved (first scroll or touch). A visitor
       who reads the first screen and taps "Book" never pays for a video at all. */
    var pageLoaded = document.readyState === 'complete';
    var intent = false;
    var armed = false;
    // The theme editor has no visitor to scroll, so previews arm as soon as they load.
    if (window.Shopify && window.Shopify.designMode) intent = true;

    function arm() {
      if (armed || !pageLoaded || !intent) return;
      armed = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll, { passive: true });
      onScroll();
    }
    function onIntent() { intent = true; arm(); }
    if (!pageLoaded) {
      window.addEventListener('load', function () { pageLoaded = true; arm(); }, { once: true });
    }
    window.addEventListener('scroll', onIntent, { passive: true, once: true });
    window.addEventListener('touchstart', onIntent, { passive: true, once: true });
    arm();
  }

  /* ---------------------------------------------------------------- *
   * Magnetic CTA — opt-in. The button eases toward the cursor.
   * ---------------------------------------------------------------- */
  function initMagnetic(el) {
    if (el.__kbMag) return; el.__kbMag = true;
    var strength = parseFloat(el.getAttribute('data-kb-magnetic')) || 0.22;
    var rect = null, raf = null, tx = 0, ty = 0, gx = 0, gy = 0;
    function tick() {
      tx = lerp(tx, gx, 0.2); ty = lerp(ty, gy, 0.2);
      el.style.transform = (gx === 0 && gy === 0 && Math.abs(tx) < 0.15 && Math.abs(ty) < 0.15)
        ? '' : 'translate(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px)';
      if (Math.abs(tx - gx) > 0.12 || Math.abs(ty - gy) > 0.12) { raf = requestAnimationFrame(tick); } else { raf = null; }
    }
    function run() { if (raf == null) raf = requestAnimationFrame(tick); }
    el.addEventListener('pointerenter', function () { rect = el.getBoundingClientRect(); });
    el.addEventListener('pointermove', function (e) {
      if (!rect) rect = el.getBoundingClientRect();
      gx = (e.clientX - (rect.left + rect.width / 2)) * strength;
      gy = (e.clientY - (rect.top + rect.height / 2)) * strength;
      run();
    });
    el.addEventListener('pointerleave', function () { gx = 0; gy = 0; run(); });
  }

  /* ---------------------------------------------------------------- *
   * Soft tilt — opt-in.
   * ---------------------------------------------------------------- */
  function initTilt(el) {
    if (el.__kbTilt) return; el.__kbTilt = true;
    var max = parseFloat(el.getAttribute('data-kb-tilt')) || 5;
    var rect = null;
    el.style.transition = 'transform .3s cubic-bezier(0.22,1,0.36,1)';
    el.addEventListener('pointerenter', function () { rect = el.getBoundingClientRect(); });
    el.addEventListener('pointermove', function (e) {
      if (!rect) rect = el.getBoundingClientRect();
      var px = (e.clientX - rect.left) / rect.width - 0.5;
      var py = (e.clientY - rect.top) / rect.height - 0.5;
      el.style.transform = 'perspective(900px) rotateX(' + (-py * max).toFixed(2) + 'deg) rotateY(' + (px * max).toFixed(2) + 'deg)';
    });
    el.addEventListener('pointerleave', function () { el.style.transform = ''; });
  }

  function init() {
    if (!motionOK) return; // reduced-motion: clean static cards
    if (mqFine) {
      // Desktop / fine pointer: hover-to-play + opt-in magnetics & tilt.
      each(document.querySelectorAll('[data-kb-hover-video]'), initHoverVideo);
      each(document.querySelectorAll('[data-kb-magnetic]'), initMagnetic);
      each(document.querySelectorAll('[data-kb-tilt]'), initTilt);
    } else if (!connectionIsExpensive()) {
      // Touch / mobile: ONE video plays at a time — the card you are centred on — and only
      // after the page has loaded and the visitor has started scrolling (see arm()).
      initScrollVideoController(document.querySelectorAll('[data-kb-hover-video]'));
    }
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); }
  else { init(); }
  if (window.Shopify && window.Shopify.designMode) { document.addEventListener('shopify:section:load', init); }
})();
